import React from 'react'
import clsx from 'clsx'
import { format as formatDate, isToday, isYesterday, isTomorrow } from 'date-fns'
import { useAppDispatch } from '@SonarCX/hooks/redux'

import Calendar from '@SonarCX/components/atoms/Calendar'

import { formatDateRelative } from '@SonarCX/utilities'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import useTaskPermissions from '@SonarCX/hooks/permissions/useTaskPermissions'
import useTaskInputOpen from '@SonarCX/hooks/useTaskInputOpen'
import useDetectNihalife from '@SonarCX/hooks/useDetectInstance/useDetectNihalife'
import useStyles from './styles'

const MOBILE_FORMAT = 'MM/dd/yy'

const formatNihalifeDate = date => {
  const NIHALIFE_FORMAT = 'MMM d, yyyy'
  const dateValue = new Date(date)
  const formatted = formatDate(dateValue, NIHALIFE_FORMAT)
  let suffix = ""
  if (isToday(dateValue)) {
    suffix = "Today"
  } else if (isYesterday(dateValue)) {
    suffix = "Yesterday"
  } else if (isTomorrow(dateValue)) {
    suffix = "Tomorrow"
  }

  if (suffix) {
    return `${formatted} (${suffix})`
  }

  return formatted
}

const DueDateComponent = ({ task, mobile = false }) => {
  const dispatch = useAppDispatch()

  const { updateTask } = useTaskAPI()
  const { canEdit } = useTaskPermissions(task)
  const { isOpen, handleClick, handleClose } = useTaskInputOpen(
    task,
    'DUE_DATE'
  )
  const { isNihalife } = useDetectNihalife()

  const [startingValue, setStartingValue] = React.useState(task.due_date)
  const [value, setValue] = React.useState(startingValue)

  const calendarRef = React.useRef()
  const formattedDate = React.useMemo(() => {
    const dateValue = new Date(value) ?? new Date()
    if (mobile) {
      return formatDate(dateValue, MOBILE_FORMAT)
    }

    if (isNihalife) {
      return formatNihalifeDate(dateValue)
    }
    
    return formatDateRelative(dateValue, true)
  }, [mobile, value, isNihalife])

  const complete = React.useMemo(
    () => Boolean(task.completed_at),
    [task.completed_at]
  )
  const completeDate = React.useMemo(() => {
    if (!complete) {
      return ''
    }

    if (mobile) {
      return formatDate(new Date(task.completed_at), MOBILE_FORMAT)
    }

    if (isNihalife) {
      return formatNihalifeDate(task.completed_at)
    }

    return formatDateRelative(task.completed_at, true)
  }, [complete, mobile, isNihalife, task.completed_at])
  const isDue = React.useMemo(
    () => !complete && new Date(value) < new Date(),
    [complete, value]
  )

  const classes = useStyles({ isDue, canEdit })

  React.useEffect(() => {
    setStartingValue(task.due_date)
    setValue(task.due_date)
  }, [task.due_date])

  const handleBlur = React.useCallback(
    async value => {
      const startDate = new Date(startingValue).toISOString()
      const currDate = new Date(value).toISOString()
      if (startDate !== currDate) {
        try {
          const newTask = { ...task, due_date: currDate }
          await updateTask(newTask.id, newTask)
          setValue(value)
          dispatch(AddSuccessAlert('Changes saved'))
        } catch (err) {
          dispatch(AddErrorAlert({ err }))
        }
      } else {
        setValue(startingValue)
      }
    },
    [startingValue, task, updateTask, dispatch]
  )

  const handleChange = React.useCallback(
    e => {
      const newDate = e.toDate()
      const oldDate = new Date(value)
      const wasDateChanged =
        newDate.getYear() !== oldDate.getYear() ||
        newDate.getMonth() !== oldDate.getMonth() ||
        newDate.getDate() !== oldDate.getDate()

      if (wasDateChanged) {
        handleBlur(newDate)
      } else {
        setValue(newDate)
      }

      handleClose()
    },
    [handleClose, handleBlur, value]
  )

  const handleAccept = React.useCallback(
    e => {
      handleBlur(e.toDate())
      handleClose()
    },
    [handleBlur, handleClose]
  )

  return (
    <div ref={calendarRef} className={classes.container}>
      {complete ? (
        <div
          style={{ cursor: 'default' }}
          className={clsx(mobile && classes.completed)}
        >
          {!mobile ? `Completed ${completeDate}` : 'Completed'}
        </div>
      ) : (
        <>
          <div
            onClick={handleClick}
            className={clsx(classes.root, isDue && 'fade')}
          >
            {formattedDate}
          </div>

          {isOpen && (
            <Calendar
              value={value}
              onChange={handleChange}
              anchorEl={calendarRef}
              onAccept={handleAccept}
              disabled={!canEdit || mobile}
              onClose={handleClose}
              open
            >
              <div />
            </Calendar>
          )}
        </>
      )}
    </div>
  )
}

export default React.memo(DueDateComponent)
